import React from 'react';

export type EvidenceCategory = 'retail' | 'inventory' | 'campaign' | 'forecast' | 'buyer' | 'other';

export interface EvidenceAssertionRow {
  id: string;
  label: string;
  metricKey: string;
  value: number | string | null;
  unit?: string;
  dataStatus: 'known' | 'unavailable';
  lineageRefIds: string[];
  notes?: string[];
}

function categoryFromMetricKey(metricKey: string): EvidenceCategory {
  const prefix = (metricKey || '').split('.')[0] || '';
  if (prefix === 'retail') return 'retail';
  if (prefix === 'inventory') return 'inventory';
  if (prefix === 'campaign') return 'campaign';
  if (prefix === 'forecast') return 'forecast';
  if (prefix === 'buyer') return 'buyer';
  return 'other';
}

function formatValue(a: EvidenceAssertionRow): string {
  if (a.dataStatus !== 'known' || a.value === null || a.value === undefined) return 'Unavailable';
  if (typeof a.value === 'number') {
    if (a.unit === '$' || a.unit === 'usd' || a.unit === 'dollars') {
      return `$${a.value.toLocaleString()}`;
    }
    if (a.unit === '%' || a.unit === 'percent' || a.unit === 'pct') {
      // Handle decimal percentages (0.12 → 12%)
      const pct = a.value < 1 && a.value > -1 ? a.value * 100 : a.value;
      return `${pct.toFixed(1)}%`;
    }
    if (a.unit === 'units') {
      return `${a.value.toLocaleString()} units`;
    }
    return `${a.value.toLocaleString()}${a.unit ? ` ${a.unit}` : ''}`;
  }
  // Handle objects - show truncated JSON preview instead of [object Object]
  if (typeof a.value === 'object') {
    try {
      const json = JSON.stringify(a.value);
      if (json.length > 50) {
        return json.substring(0, 47) + '...';
      }
      return json;
    } catch {
      return '[Complex Object]';
    }
  }
  return String(a.value);
}

export function AssertionList(props: {
  assertions: EvidenceAssertionRow[];
  selectedMetricKey?: string | null;
  onSelectAssertion: (a: EvidenceAssertionRow) => void;
}): JSX.Element {
  const { assertions, selectedMetricKey, onSelectAssertion } = props;

  const grouped: Record<EvidenceCategory, EvidenceAssertionRow[]> = {
    retail: [],
    inventory: [],
    campaign: [],
    forecast: [],
    buyer: [],
    other: []
  };

  for (const a of assertions) {
    grouped[categoryFromMetricKey(a.metricKey)].push(a);
  }

  const renderGroup = (title: string, items: EvidenceAssertionRow[]) => {
    if (items.length === 0) return null;
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800">{title}</div>
        <div className="divide-y divide-gray-100">
          {items.map((a) => (
            <button
              key={a.id}
              onClick={() => onSelectAssertion(a)}
              className={`w-full text-left px-4 py-3 hover:bg-violet-50 transition-colors ${
                selectedMetricKey && a.metricKey === selectedMetricKey ? 'bg-violet-50' : 'bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{a.label}</div>
                  <div className="text-xs text-gray-500 font-mono truncate">{a.metricKey}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold text-gray-900">{formatValue(a)}</div>
                  <div className={`text-xs ${a.dataStatus === 'known' ? 'text-green-700' : 'text-gray-500'}`}>
                    {a.dataStatus === 'known' ? 'Known' : 'Unavailable'}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {renderGroup('Retail', grouped.retail)}
      {renderGroup('Inventory', grouped.inventory)}
      {renderGroup('Campaign', grouped.campaign)}
      {renderGroup('Forecast', grouped.forecast)}
      {renderGroup('Buyer', grouped.buyer)}
      {renderGroup('Other', grouped.other)}
    </div>
  );
}


